<template>
  <div
    v-if="!readonly || hasCaption"
    class="caption-rail"
    data-cy="caption-rail"
  >
    <div class="rail-head">
      <span class="rail-title">Caption</span>
    </div>

    <!-- Edit: prefilled textarea + counter + Save/Cancel -->
    <template v-if="!readonly && editing">
      <textarea
        ref="textareaRef"
        v-model="draft"
        class="cap-input"
        :maxlength="maxLength"
        placeholder="Describe what this shows…"
        data-cy="caption-input"
      ></textarea>
      <div class="rail-foot">
        <span
          class="charcount"
          :class="{ near: nearLimit }"
          data-cy="caption-count"
        >
          {{ draft.length }} / {{ maxLength }}
        </span>
        <span class="flex gap-2">
          <SeraButton
            type="secondary"
            class="!px-3 !py-1.5 text-sm"
            data-cy="caption-cancel"
            @click="cancel"
          >
            Cancel
          </SeraButton>
          <SeraButton
            class="!px-3 !py-1.5 text-sm"
            data-cy="caption-save"
            @click="save"
          >
            Save
          </SeraButton>
        </span>
      </div>
    </template>

    <!-- Read: tinted quote + grouped Edit / Delete -->
    <template v-else-if="hasCaption">
      <blockquote class="cap-quote" data-cy="caption-text">
        {{ caption }}
      </blockquote>
      <div v-if="!readonly" class="rail-actions">
        <button
          type="button"
          class="btn-link"
          data-cy="caption-edit"
          @click="startEdit"
        >
          <i class="fa-regular fa-pen-to-square"></i> Edit
        </button>
        <button
          type="button"
          class="icon-ghost danger"
          title="Delete caption"
          data-cy="caption-delete"
          @click="remove"
        >
          <i class="fa-regular fa-trash-alt"></i>
        </button>
      </div>
    </template>

    <!-- Empty: prompt to add -->
    <template v-else>
      <div class="cap-empty" data-cy="caption-empty">No caption yet.</div>
      <div class="rail-actions">
        <button
          type="button"
          class="btn-link"
          data-cy="caption-add"
          @click="startEdit"
        >
          <i class="fa-regular fa-pen-to-square"></i> Add caption
        </button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";

import SeraButton from "@/components/Button.vue";

const props = withDefaults(
  defineProps<{
    caption?: string | null;
    openMode?: "read" | "edit";
    maxLength?: number;
    itemId?: number | string | null;
    readonly?: boolean;
  }>(),
  {
    caption: null,
    openMode: "read",
    maxLength: 300,
    itemId: null,
    readonly: false
  }
);

const emit = defineEmits<{
  save: [caption: string];
  delete: [];
}>();

const editing = ref(props.openMode === "edit");
const draft = ref(props.caption ?? "");
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const hasCaption = computed(
  () => !!props.caption && props.caption.trim().length > 0
);
const nearLimit = computed(() => props.maxLength - draft.value.length <= 100);

// The parent drives open mode per item (pen-on-empty → edit; pen/eye on an
// existing caption → read; prev/next resets to read). Key the reset on the item
// as well as the mode: a stale in-progress edit must not carry over when the
// viewer switches to a different item, and successive opens can leave openMode
// unchanged (both "read"), so watching openMode alone would miss the switch.
watch(
  [() => props.itemId, () => props.openMode],
  () => {
    editing.value = props.openMode === "edit";
    if (editing.value) {
      draft.value = props.caption ?? "";
      focusInput();
    }
  }
);

async function focusInput() {
  await nextTick();
  const ta = textareaRef.value;
  if (!ta) return;
  ta.focus();
  ta.setSelectionRange(ta.value.length, ta.value.length);
}

function startEdit() {
  draft.value = props.caption ?? "";
  editing.value = true;
  focusInput();
}

function cancel() {
  draft.value = props.caption ?? "";
  editing.value = false;
}

function save() {
  emit("save", draft.value.trim());
  editing.value = false;
}

function remove() {
  emit("delete");
  editing.value = false;
}

onMounted(() => {
  if (editing.value) focusInput();
});
</script>

<style scoped>
.caption-rail {
  @apply flex flex-col;
}

.rail-head {
  @apply mb-3;
}

.rail-title {
  @apply text-base font-bold text-gray-900;
}

.cap-input {
  @apply w-full rounded-md border border-gray-300 p-3 text-sm;
  min-height: 120px;
  resize: vertical;
}

/* Taller field on desktop, where the rail sits beside the media. */
@media (min-width: 1024px) {
  .cap-input {
    min-height: 220px;
  }
}

.cap-input:focus {
  @apply outline-none border-blue-600 ring-1 ring-blue-600;
}

.rail-foot {
  @apply flex items-center justify-between mt-3;
}

.charcount {
  @apply text-xs text-gray-500;
}

.charcount.near {
  @apply text-amber-600;
}

/* Read state — tinted quote with a primary left-accent (locked design). */
.cap-quote {
  @apply m-0 rounded-r-md p-3 text-sm whitespace-pre-wrap text-gray-900;
  background: #f6f8fb;
  border-left: 3px solid #2563eb;
  line-height: 1.5;
  word-wrap: break-word;
}

.cap-empty {
  @apply text-sm italic text-gray-400;
}

.rail-actions {
  @apply flex items-center gap-1 mt-3;
}

.btn-link {
  @apply inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 mr-auto bg-transparent border-0 p-0 cursor-pointer;
}

.btn-link:hover {
  @apply underline;
}

.icon-ghost {
  @apply inline-flex p-1.5 rounded text-gray-500 bg-transparent border-0 cursor-pointer transition-colors;
}

.icon-ghost.danger:hover {
  @apply text-red-700 bg-red-50;
}
</style>
